<script lang="ts" setup>
import { AbcIcon } from "@ordbogen-npm/abc-dsk";
import { computed, readonly, toRefs } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import type { Color, Gradient } from "@/abc-dsk/options";
import { mapColorToGradient } from "@/abc-dsk/utils";
import { unpackAchievementBadge } from "@/common/composables/use-vocab-achievements";
import {
  type Achievement,
  AchievementItemEffectType,
  AchievementType,
  type UnpackedBadge
} from "@/common/types/vocab";

import AchievementBadge from "./AchievementBadge.vue";
import AchievementItemEffect from "./AchievementItemEffect.vue";
import AchievementItemIcon from "./AchievementItemIcon.vue";
import AchievementItemProgressBar from "./AchievementItemProgressBar.vue";

const props = defineProps<{
  achievement: Achievement;
  showStepsInName: boolean;
}>();

const emit = defineEmits(["redeemClick"]);

const { t } = useI18n();

const { title, description, color, progress, steps, type } = readonly(toRefs(props.achievement));

const achievedAt = computed<Date | null>(() =>
  props.achievement.achievedAt ? new Date(props.achievement.achievedAt) : null
);

const badge = computed<UnpackedBadge | null>(() => {
  if (!props.achievement.badge) {
    return null;
  }

  return unpackAchievementBadge(props.achievement);
});

const achievementDisplayName = computed<string>(() => {
  if (props.showStepsInName) {
    return `${title} ${steps}`;
  }

  return title;
});

enum AchievementItemState {
  Ongoing = 1,
  Unopened = 2,
  Opened = 3
}

const state = computed<AchievementItemState>(() => {
  if (badge.value?.openedAt) {
    return AchievementItemState.Opened;
  }

  if (achievedAt.value) {
    return AchievementItemState.Unopened;
  }

  return AchievementItemState.Ongoing;
});

const isCampaign = computed<boolean>(() => type === AchievementType.Campaign);

const achievementTextColor = computed<Color | "white">(() => {
  if (!isCampaign.value) {
    return color;
  }

  return state.value === AchievementItemState.Unopened ? "white" : color;
});

const openBadgeBtnColor = computed<Color | "white">(() => (isCampaign.value ? "white" : color));

const progressBarGradient = computed<Gradient | undefined>(() => {
  if (!isCampaign.value) {
    return undefined;
  }

  return state.value === AchievementItemState.Opened ? mapColorToGradient(color) : undefined;
});

const effectName = computed<AchievementItemEffectType | undefined>(() =>
  isCampaign.value ? (`twinkling-stars-${color}` as AchievementItemEffectType) : undefined
);
</script>

<template>
  <article
    class="achievement-item"
    :class="{ [`achievement-item--${color}`]: state === AchievementItemState.Unopened }"
  >
    <AchievementItemEffect v-if="effectName" :effect="effectName" />
    <div class="achievement-item__wrapper">
      <AchievementBadge v-if="badge?.openedAt" :badge="badge" responsive />
      <AchievementItemIcon
        v-else
        size="md"
        :color="color"
        :notification="state === AchievementItemState.Unopened ? true : false"
      />
      <div>
        <h2
          class="achievement-item__title"
          :class="`achievement-item__title--${achievementTextColor}`"
          lang="en"
        >
          {{ achievementDisplayName }}
        </h2>
        <p
          v-if="description"
          class="achievement-item__text"
          :class="`achievement-item__text--${achievementTextColor}`"
        >
          {{ description }}
        </p>
      </div>
    </div>
    <div>
      <AchievementItemProgressBar
        :color="color"
        :white-text="achievementTextColor === 'white'"
        :gradient="progressBarGradient"
        :steps="steps"
        :progress="progress"
        progress-position="left"
        class="achievement-item__progress-bar"
      />
      <div v-if="state === AchievementItemState.Ongoing" class="text-md-right">
        <TgButton variant="text" size-sm="sm" size-md="md" :color="color" disabled>
          <span class="achievement-item__button-text">
            {{ t("vocab.achievements.item.ongoingButton") }}
          </span>
        </TgButton>
      </div>
      <div v-else-if="state === AchievementItemState.Unopened" class="text-md-right">
        <TgButton
          variant="text"
          size-sm="sm"
          size-md="md"
          :color="openBadgeBtnColor"
          @click="emit('redeemClick')"
        >
          <span class="achievement-item__button-text">
            {{ t("vocab.achievements.item.openButton") }}
          </span>
        </TgButton>
      </div>
      <div
        v-else-if="state === AchievementItemState.Opened"
        class="d-flex align-items-center justify-content-md-end"
      >
        <span class="achievement-item__text achievement-item__opened-at-text">
          {{
            t("vocab.achievements.item.achievedAt", {
              date: achievedAt?.toLocaleDateString("da", {
                dateStyle: "medium"
              })
            })
          }}
        </span>
        <AbcIcon
          name="check-mark"
          :color="color"
          width="24"
          height="24"
          class="achievement-item__checkmark-icon mt-n1"
        />
      </div>
    </div>
  </article>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.achievement-item {
  display: grid;
  position: relative;
  gap: 6px;
  grid-template-columns: 1fr;
  align-items: center;
  padding: 14px 20px;
  overflow: hidden;
  border: 1px solid rgb(@theme-colors[@grey][50]);
  border-radius: 8px;
  background: @colors[@white];

  #media-breakpoint-up(lg, {
    gap: 14px;
    grid-template-columns: 1.5fr 1fr;
    padding: 24px;
  });

  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      border-color: rgb(@value[base]);
      background: rgb(@value[10]);
    }
  });

  // Special colors/gradients which has a slightly more exclusive appearance.
  each(@theme-gradients, {
    @name: replace(@key, "@", "");

    &--@{name} {
      border-color: rgb(@theme-colors[ @@name][110]) !important; // @@name resolves the value of @name instead of literal value `@name`
      background: @value[linear] !important;
    }
  });

  &__wrapper {
    display: flex;
    gap: 14px;
    align-items: center;
  }

  &__title {
    margin-bottom: 6px;
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;

    each(@theme-colors, {
      @name: replace(@key, "@", "");

      &--@{name} {
        color: rgb(@value[base]);
      }
    });

    &--white {
      color: @colors[@white];
    }
  }

  &__text {
    margin-bottom: 0;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;

    &--white {
      color: @colors[@white];
    }
  }

  &__progress-bar {
    margin-bottom: 8px;
  }

  &__opened-at-text {
    margin-top: 5px;
    color: rgb(@theme-colors[@grey][base]);
  }

  &__checkmark-icon {
    min-width: 24px;
    min-height: 24px;
    margin-right: -3px;
    margin-left: 8px;
  }

  &__button-text {
    word-break: initial;
    white-space: nowrap;
  }
}
</style>
